import React from 'react';
import { ChevronDown, Filter, Search } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../utils/cn';

interface AdminFiltersPanelProps {
  children: React.ReactNode;
  className?: string;
  columns?: 2 | 3 | 4 | 5;
}

export const AdminFiltersPanel: React.FC<AdminFiltersPanelProps> = ({
  children,
  className,
  columns = 4,
}) => {
  const gridClass =
    columns === 2
      ? 'md:grid-cols-2'
      : columns === 3
        ? 'md:grid-cols-3'
        : columns === 4
          ? 'md:grid-cols-4'
          : 'md:grid-cols-2 xl:grid-cols-5';

  return (
    <div
      className={cn(
        'mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm',
        className
      )}
    >
      <div className={cn('grid grid-cols-1 gap-4', gridClass)}>{children}</div>
    </div>
  );
};

interface AdminFilterSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const AdminFilterSearch: React.FC<AdminFilterSearchProps> = ({
  value,
  onChange,
  placeholder = 'Buscar...',
}) => {
  return (
    <div className="relative">
      <Search
        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        size={20}
        aria-hidden="true"
      />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
};

interface AdminFilterSelectProps {
  value: string | number;
  onChange: (value: string) => void;
  children: React.ReactNode;
  icon?: LucideIcon;
  disabled?: boolean;
  truncate?: boolean;
  title?: string;
  'aria-label'?: string;
}

export const AdminFilterSelect: React.FC<AdminFilterSelectProps> = ({
  value,
  onChange,
  children,
  icon: Icon = Filter,
  disabled = false,
  truncate = true,
  title,
  'aria-label': ariaLabel,
}) => {
  return (
    <div className="relative min-w-0">
      <Icon
        className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-gray-400"
        size={20}
        aria-hidden="true"
      />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-label={ariaLabel}
        title={title}
        className={cn(
          'w-full min-w-0 appearance-none rounded-lg border border-gray-300 py-2 pl-10 pr-10 focus:border-transparent focus:ring-2 focus:ring-blue-500',
          'disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500',
          truncate && 'overflow-hidden text-ellipsis whitespace-nowrap'
        )}
      >
        {children}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
        size={18}
        aria-hidden="true"
      />
    </div>
  );
};
